import { CheckIcon } from '@heroicons/react/outline';
import { Affix, Tag } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import LogoSquare from 'assets/Icons/LogoSquare';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

// import { usePayment } from '@/components/CartPage/RazorPay';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import CustomLoader from '@/components/shared/Loading';
import { notify } from '@/components/shared/notify';

import { user } from '@/atoms/user';
import {
  getSubscriptionInfo,
  // subscriptionPaymentVerify,
  verifySubscription,
} from '@/services/Subscribe';
import { IGetSubscriptionInfo } from '@/utils/interfaces/Subscription';

type Props = {
  merchant?: string;
  title?: string;
  purpose?: string;
  currency?: string;
  amount?: number;
};

type PaymentSuccessCardProps = {
  amount: number;
  currency: string;
  screens: any;
  userName: string;
  razorpayOrderId: string;
};

const PaymentSuccessCard = ({
  amount,
  currency,
  screens,
  razorpayOrderId,
}: PaymentSuccessCardProps) => {
  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
  }).format(amount);

  return (
    <>
      <ShouldRender check={screens?.lg}>
        <div className='w-full max-w-xl mx-auto bg-[#003d23] shadow-sm px-6 py-8 text-center'>
          <div className='mx-auto mb-4 h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center'>
            <CheckIcon className='w-8 h-8 text-[#003d23]' />
          </div>

          <h3 className='text-lg font-semibold text-zinc-200'>
            Payment Completed
          </h3>

          <p className='mt-2 text-sm text-zinc-200'>
            {'You have successfully paid '}
            <span className='font-medium text-zinc-200'>{formatted}</span>
          </p>

          <p className='mt-3 text-xs text-zinc-200'>
            {'Order ID: '}
            {razorpayOrderId}
          </p>
        </div>
      </ShouldRender>
      <ShouldRender check={!screens?.lg}>
        <div className=' max-w-[360px] p-4 text-white'>
          <div>
            <p className='text-[18px] font-semibold leading-tight'>
              Payment Request from
            </p>
            <p className='mt-1 text-[18px] font-extrabold tracking-wide'>
              ZEBRALEARN
            </p>

            <p className='mt-4 text-[11px] uppercase text-neutral-400 tracking-wide'>
              Amount Payable
            </p>

            <div className='mt-1 flex items-baseline'>
              <p className='text-[22px] font-extrabold leading-none'>
                {formatted}
              </p>
              <Tag
                bordered
                className='-rotate-12 ml-2 border-2 border-red-600 bg-red-900/20 text-red-400 px-2 py-0.5 leading-none uppercase font-semibold tracking-wider rounded -translate-x-4 -translate-y-2'
              >
                PAID
              </Tag>
            </div>

            <div className='mt-2 h-[3px] w-10 rounded bg-emerald-400' />

            <p className='mt-6 text-[11px] uppercase text-neutral-400 tracking-wide'>
              Order ID
            </p>
            <p className='mt-1 text-[14px] text-neutral-300 tracking-wide'>
              {razorpayOrderId}
            </p>
          </div>
        </div>
      </ShouldRender>
    </>
  );
};

const formatAmount = (amount: number, currency: string, mobile = false) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: mobile ? 2 : 0,
    maximumFractionDigits: mobile ? 2 : 0,
  }).format(amount);
};

const Ribbon = ({ text }: { text: string }) => (
  <div className='rounded-t-xl text-xs lg:rounded-tr-none lg:rounded-tl-xl border-b border-[#1e2328] bg-[#2F2806] px-3 md:px-6 py-3 text-[13px] leading-5 text-[#c6ab7b]'>
    {text}
  </div>
);

const PaymentInfo = ({
  title,
  purpose,
  amount,
  currency,
  mobile,
}: {
  title: string;
  purpose: string;
  amount: number;
  currency: string;
  mobile?: boolean;
}) => (
  <div className='px-4 lg:px-8 py-4 lg:py-7'>
    <h2 className='font-semibold text-white/95 text-[17px] md:text-xl'>
      {title}
    </h2>

    <div className='mt-5 lg:mt-8 space-y-5 lg:space-y-6'>
      <div>
        <div className='text-[11px] lg:text-[12px] font-semibold uppercase tracking-wide text-zinc-400'>
          Billed To
        </div>
        <div className='mt-1 text-[15px] lg:text-base text-white/95'>
          {purpose}
        </div>
      </div>

      <div>
        <div className='text-[11px] lg:text-[12px] font-semibold uppercase tracking-wide text-zinc-400'>
          Amount Payable
        </div>
        <div className='mt-1 font-semibold text-white/95 text-xl md:text-2xl'>
          {formatAmount(amount, currency, mobile)}
        </div>
        <div className='mt-2 h-[3px] w-8 rounded-sm bg-emerald-500' />
      </div>
    </div>
  </div>
);

const CheckoutZebraLearn = ({
  merchant = 'ZEBRALEARN',
  title = 'Payment Request from ZEBRALEARN',
  currency = 'INR',
}: Props) => {
  const [order, setOrder] = useState<IGetSubscriptionInfo>();
  const [loading, setLoading] = useState(false);
  const [paymentloading, setPaymentLoading] = useState(false);
  const screens = useBreakpoint();
  const router = useRouter();
  const { orderId } = router.query;
  // const { initializeShortRazorpay } = usePayment();
  const [userData] = useRecoilState(user);

  const fetchCoupons = async () => {
    try {
      setLoading(true);

      const data = await getSubscriptionInfo(orderId as string);
      setOrder(data.data);

      // setCoupons(data?.data?.coupons ?? []);
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchCoupons();
    }
  }, [orderId]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (typeof document === 'undefined') {
        resolve(false);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // const handleProceedPayment = () => {
  //   initializeShortRazorpay({
  //     amount: (order?.amount as number) * 100 || 0,
  //     razorpayOrderId: order?.rzp_order_id || '',
  //     orderId: order?.rzp_order_id || '',
  //     userData: {
  //       name: order?.user_name || '',
  //       email: order?.user_email || '',
  //     },
  //     onSuccess: (response) => {
  //       subscriptionPaymentVerify(
  //         response.razorpay_payment_id,
  //         orderId as string,
  //       );
  //       setOrder((prev) => ({
  //         ...prev!,
  //         status: 'paid',
  //         rzp_order_id: response?.razorpay_payment_id || '',
  //       }));
  //     },
  //     onFailure: () => {
  //       notify('Payment Failed. Please try again.', 'error');
  //     },
  //   });
  // };

  const handleProceedPaymentV2 = async () => {
    const scriptLoaded = await loadRazorpayScript();

    if (!scriptLoaded) {
      notify('Razorpay SDK failed to load. Are you online?', 'error');
      return;
    }

    const key =
      order?.payment_account === 'razorpay_2'
        ? process.env.NEXT_PUBLIC_RAZORPAY_KEY_2
        : process.env.NEXT_PUBLIC_RAZORPAY_KEY;

    if (!key) {
      notify('PUBLIC_RAZORPAY_KEY not present');
      return;
    }

    const options = {
      key: key,
      ...(order?.mode === 'auto'
        ? { subscription_id: order?.rzp_subscription_id }
        : { order_id: order?.rzp_order_id }),
      name: 'Zebralearn',
      description: 'Subscription',
      prefill: {
        name: userData?.name,
        email: userData?.email,
      },
      modal: {
        ondismiss: () => {
          notify('Payment Failed. Please try again.', 'error');
        },
      },
      handler: async function (response: {
        razorpay_payment_id: string;
        razorpay_subscription_id: string;
        razorpay_signature: string;
      }) {
        try {
          setPaymentLoading(true);
          const verifyWithRetry = async () => {
            let shouldRetry = true;
            let verifyResult: any;

            while (shouldRetry) {
              verifyResult = await verifySubscription({
                rp_payment_id: response.razorpay_payment_id,
                ...(order?.mode === 'auto'
                  ? {
                      razorpay_subscription_id:
                        response.razorpay_subscription_id,
                    }
                  : {}),
              });

              const retry = verifyResult?.data?.retry;
              const retryAfter = verifyResult?.data?.retry_after;

              if (retry && retryAfter) {
                const waitMs = retryAfter * 1000;
                await new Promise((res) => setTimeout(res, waitMs));
                continue;
              }

              shouldRetry = false;
            }

            return verifyResult;
          };

          await verifyWithRetry();
          setOrder((prev) => ({
            ...prev!,
            status: 'paid',
            rzp_order_id: response?.razorpay_payment_id || '',
          }));
        } catch (error: any) {
          notify(formateErrorObject(error), 'error');
        } finally {
          setPaymentLoading(false);
        }
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const ProceedButton = ({
    size,
    onClick,
  }: {
    size: any;
    onClick?: () => void;
  }) => (
    <ButtonDefault
      loading={paymentloading}
      disabled={paymentloading}
      size={size}
      rounded={false}
      onClick={onClick}
      roundedSm
      id='secure_checkout'
      enableScaling={false}
      variant={ButtonVariants.PRIMARY}
      shimmerButton
      className='w-full text-center p-3 text-sm bg-fadedWhite disabled:cursor-not-allowed text-white md:p-2 disabled:bg-opacity-50 disabled:text-[#3E3E3E]'
    >
      <span className='text-sm md:text-base py-[3px] md:py-[4px] w-full font-semibold whitespace-nowrap'>
        Proceed to Pay
      </span>
    </ButtonDefault>
  );

  return (
    <>
      <div className='lg:hidden min-h-svh w-full bg-cartBgColor'>
        <Affix offsetTop={0}>
          <header className='flex items-center gap-3 bg-[#0d4297] px-4 py-3 text-white'>
            <LogoSquare width='28' />
            <a
              href='https://zebralearn.com/'
              className='text-sm font-semibold tracking-wide text-white'
              target='_blank'
            >
              {merchant}
            </a>
          </header>
        </Affix>

        <main className='px-2 xxxs:px-4 py-4 mt-2 xxxs:mt-4 space-y-4'>
          <section className='rounded-xl border border-[#1e2328] bg-[#0f1214] shadow-[0_10px_30px_rgba(0,0,0,0.35)] overflow-hidden'>
            <Ribbon text='Secure Payment Link — Your transaction is protected and processed safely.' />
            <ShouldRender check={order?.status?.toLowerCase() !== 'paid'}>
              <PaymentInfo
                title={title}
                purpose={order?.user_name || 'Zebralearn Payment'}
                amount={order?.amount || 0}
                currency={currency}
                mobile
              />
            </ShouldRender>
            <ShouldRender check={order?.status?.toLowerCase() === 'paid'}>
              <PaymentSuccessCard
                screens={screens}
                amount={order?.amount || 0}
                currency='INR'
                userName={order?.user_name || ''}
                razorpayOrderId={order?.rzp_order_id || ''}
              />
            </ShouldRender>
          </section>

          <section className='rounded-xl border border-[#1e2328] bg-[#0f1214] shadow-[0_10px_30px_rgba(0,0,0,0.35)] overflow-hidden'>
            <div className='px-4 py-4'>
              <LogoSquare width='28' />
              <p className='mt-3 text-[12px] leading-5 text-zinc-300'>
                ZebraLearn — where books come alive with visuals & action.{' '}
                <a
                  href='https://zebralearn.com/'
                  className='underline decoration-0 text-zinc-300'
                  target='_blank'
                >
                  Explore now
                </a>
              </p>
            </div>
          </section>
          <div className='h-24' />
        </main>
        <ShouldRender check={order?.status?.toLowerCase() !== 'paid'}>
          <Affix offsetBottom={0}>
            <div className='bg-bgColor border-t bottom-0 absolute w-full border-[#2A2A2A] p-2 xs:p-3 flex gap-x-2 items-center'>
              <ProceedButton
                size={screens.md ? 4 : 1}
                onClick={() => handleProceedPaymentV2()}
              />
            </div>
          </Affix>
        </ShouldRender>
      </div>

      <div className='hidden lg:flex relative text-white h-screen bg-cartBgColor items-center justify-center w-full'>
        <main className='relative mx-auto px-4 py-10 lg:py-16'>
          <div className='grid items-start lg:grid-cols-[minmax(520px,1fr)_390px]'>
            <div className='flex items-center h-full w-full justify-end'>
              <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className='rounded-l-xl border border-[#1e2328] bg-[#0f1214] shadow-[0_10px_30px_rgba(0,0,0,0.35)]'
              >
                <Ribbon text='Secure Payment Link — Your transaction is protected and processed safely.' />
                <PaymentInfo
                  title={title}
                  purpose={order?.user_name || 'Zebralearn Payment'}
                  amount={order?.amount || 0}
                  currency={currency}
                />
              </motion.section>
            </div>

            <motion.aside
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut', delay: 0.05 }}
              className='relative w-full h-full  min-h-[388px]'
            >
              <div className='overflow-hidden rounded-xl h-full border border-[#1e2328] bg-[#111417] shadow-[0_10px_30px_rgba(0,0,0,0.35)]'>
                <ShouldRender check={order?.status?.toLowerCase() !== 'paid'}>
                  <div className='flex flex-col items-center gap-3 bg-[#1658b3] px-6 py-8 text-white'>
                    <LogoSquare width='64' />
                    <a
                      href='https://zebralearn.com/'
                      className='text-sm font-semibold tracking-wide text-white'
                      target='_blank'
                    >
                      {merchant}
                    </a>
                    <div className='mt-1 text-xs text-white/80 font-medium'>
                      Total Amount
                    </div>
                    <div className='text-4xl font-extrabold tracking-tight'>{`₹${order?.amount ? order?.amount?.toLocaleString('en-IN') : 0}`}</div>
                  </div>

                  <div className='h-px w-full bg-white/10' />

                  <div className='px-6 pb-6 pt-5'>
                    <ProceedButton
                      size={screens.md ? 4 : 1}
                      onClick={() => handleProceedPaymentV2()}
                    />
                    <p className='mt-3 text-center text-[12px] leading-5 text-zinc-400'>
                      By proceeding, I agree to Zebralearn{' '}
                      <a
                        className='underline decoration-dotted hover:text-zinc-300'
                        href='https://zebralearn.com/Refund_Policy.html'
                        target='_blank'
                      >
                        Refund Policy
                      </a>
                    </p>
                    <p className='text-center text-[12px] text-zinc-400'>
                      •{' '}
                      <a
                        className='underline decoration-dotted hover:text-zinc-300'
                        href='https://zebralearn.com/terms_and_conditions.html'
                        target='_blank'
                      >
                        Terms & Conditions
                      </a>
                    </p>
                  </div>
                </ShouldRender>
                <ShouldRender check={order?.status?.toLowerCase() === 'paid'}>
                  <div className='h-full '>
                    <header className='flex items-center gap-3 bg-[#0d4297] px-4 py-3 text-white'>
                      <LogoSquare width='28' />
                      <a
                        href='https://zebralearn.com/'
                        className='text-sm font-semibold tracking-wide text-white'
                        target='_blank'
                      >
                        {merchant}
                      </a>
                    </header>
                    <div className='flex items-center h-full '>
                      <PaymentSuccessCard
                        screens={screens}
                        amount={order?.amount || 0}
                        currency='INR'
                        userName={order?.user_name || ''}
                        razorpayOrderId={order?.rzp_order_id || ''}
                      />
                    </div>
                    <div></div>
                  </div>
                </ShouldRender>
              </div>
            </motion.aside>
          </div>
        </main>
      </div>

      <ShouldRender check={loading}>
        <CustomLoader />
      </ShouldRender>
    </>
  );
};

export default CheckoutZebraLearn;
